import { create } from 'zustand';
import type { Card } from '@callbridge/engine';
import { type ReconnectPayload } from '@callbridge/ui';
import {
  type GameState,
  type GamePlayer,
  type RoundResultEntry,
  initialGameState,
  setGameStarted,
  setHand,
  setCallPhase,
  setPlayPhase,
  applyCallMade,
  applyCardPlayed,
  undoCardPlayed,
  applyTrickWon,
  clearTrick,
  setRoundEnd,
  setNewRound,
  setGameOver,
  setGameAbandoned,
  setTurnTimer,
  setCurrentTurn,
  setHoveredCard,
  setSelectedCard,
  setError,
  setPlayerDisconnected,
  setPlayerReconnected,
  setPlayerReplacedByAI,
  resetGame,
  setReconnectedState,
  setPlayerVoids,
} from '@callbridge/ui';

interface GameActions {
  gameStarted: (gameId: string, players: GamePlayer[], localPlayerIndex: number) => void;
  handReceived: (hand: Card[]) => void;
  callPhaseStarted: (currentTurnIndex: number) => void;
  playPhaseStarted: (currentTurnIndex: number) => void;
  callMade: (playerId: string, call: number) => void;
  cardPlayed: (playerId: string, card: Card) => void;
  cardPlayUndone: (playerId: string, card: Card) => void;
  trickWon: (winnerId: string) => void;
  trickCleared: () => void;
  roundEnded: (results: RoundResultEntry[]) => void;
  newRoundStarted: (roundNumber: number, currentTurnIndex: number) => void;
  gameEnded: (winnerId: string, finalScores: Record<string, number>) => void;
  gameAbandoned: () => void;
  turnTimerUpdated: (remainingMs: number) => void;
  currentTurnChanged: (currentTurnIndex: number) => void;
  cardHovered: (index: number) => void;
  cardSelected: (index: number) => void;
  errorSet: (error: string | null) => void;
  playerDisconnected: (playerId: string) => void;
  playerReconnected: (playerId: string) => void;
  playerReplacedByAI: (playerId: string) => void;
  reconnectedToGame: (payload: ReconnectPayload) => void;
  voidsReceived: (voids: Record<string, string[]>) => void;
  reset: () => void;
}

export type GameStore = GameState & GameActions;

export const useGameStore = create<GameStore>()((set) => ({
  ...initialGameState,

  gameStarted: (gameId, players, localPlayerIndex) =>
    set((s) => setGameStarted(s, gameId, players, localPlayerIndex)),
  handReceived: (hand) => set((s) => setHand(s, hand)),
  callPhaseStarted: (i) => set((s) => setCallPhase(s, i)),
  playPhaseStarted: (i) => set((s) => setPlayPhase(s, i)),
  callMade: (pid, call) => set((s) => applyCallMade(s, pid, call)),
  cardPlayed: (pid, card) => set((s) => applyCardPlayed(s, pid, card)),
  cardPlayUndone: (pid, card) => set((s) => undoCardPlayed(s, pid, card)),
  trickWon: (wid) => set((s) => applyTrickWon(s, wid)),
  trickCleared: () => set((s) => clearTrick(s)),
  roundEnded: (r) => set((s) => setRoundEnd(s, r)),
  newRoundStarted: (rn, ti) => set((s) => setNewRound(s, rn, ti)),
  gameEnded: (wid, fs) => set((s) => setGameOver(s, wid, fs)),
  gameAbandoned: () => set((s) => setGameAbandoned(s)),
  turnTimerUpdated: (ms) => set((s) => setTurnTimer(s, ms)),
  currentTurnChanged: (i) => set((s) => setCurrentTurn(s, i)),
  cardHovered: (i) => set((s) => setHoveredCard(s, i)),
  cardSelected: (i) => set((s) => setSelectedCard(s, i)),
  errorSet: (err) => set((s) => setError(s, err)),
  playerDisconnected: (pid) => set((s) => setPlayerDisconnected(s, pid)),
  playerReconnected: (pid) => set((s) => setPlayerReconnected(s, pid)),
  playerReplacedByAI: (pid) => set((s) => setPlayerReplacedByAI(s, pid)),
  reconnectedToGame: (payload) => set((s) => setReconnectedState(s, payload)),
  voidsReceived: (voids) => set((s) => setPlayerVoids(s, voids)),
  reset: () => set(() => resetGame()),
}));
